import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, TimePicker, DateTimePicker as MUIDateTimePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Calendar, Clock } from 'lucide-react';
import { isValid } from 'date-fns';
import { tr, enUS, ru, kk, az } from 'date-fns/locale';

const localeMap = { tr, en: enUS, ru, kk, az };
const formatMap = {
  tr: { date: 'dd.MM.yyyy', time: 'HH:mm', datetime: 'dd.MM.yyyy HH:mm', ampm: false },
  en: { date: 'MM/dd/yyyy', time: 'hh:mm aa', datetime: 'MM/dd/yyyy hh:mm aa', ampm: true },
  ru: { date: 'dd.MM.yyyy', time: 'HH:mm', datetime: 'dd.MM.yyyy HH:mm', ampm: false },
  kk: { date: 'dd.MM.yyyy', time: 'HH:mm', datetime: 'dd.MM.yyyy HH:mm', ampm: false },
  az: { date: 'dd.MM.yyyy', time: 'HH:mm', datetime: 'dd.MM.yyyy HH:mm', ampm: false },
};

const pickerTheme = createTheme({
  typography: { fontFamily: "'Nunito Sans', sans-serif" },
  components: {
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#A0A8B4',
          marginRight: '4px',
          padding: '6px',
          '& svg': { width: '18px', height: '18px', strokeWidth: 2 },
          '&:hover': { backgroundColor: 'rgba(2, 145, 239, 0.06)', color: '#0291EF' },
          '&:focus': { outline: 'none' },
        },
      },
    },
    MuiPickersPopper: {
      styleOverrides: {
        root: {
          zIndex: 99999,
          '& .MuiPaper-root': {
            borderRadius: '6px',
            boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)',
            border: '1px solid #E8ECF0',
            marginTop: '4px',
          },
          '& *': { fontFamily: "'Nunito Sans', sans-serif !important", textTransform: 'none !important' },
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected': { backgroundColor: '#0291EF' },
          '&.Mui-selected:hover': { backgroundColor: '#0270BA' },
          '&:hover': { backgroundColor: 'rgba(2, 145, 239, 0.08)' },
        },
      },
    },
    MuiPickersYear: {
      styleOverrides: {
        yearButton: {
          '&.Mui-selected': { backgroundColor: '#0291EF' },
        },
      },
    },
    MuiPickersMonth: {
      styleOverrides: {
        monthButton: {
          '&.Mui-selected': { backgroundColor: '#0291EF' },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { color: '#0291EF', textTransform: 'none' },
      },
    },
  },
});

const DateTimePicker = ({
  value,
  onChange,
  onAccept,
  label = '',
  mode = 'date',
  required = false,
  error,
  description = '',
  minDate,
  maxDate,
}) => {
  const { i18n } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const currentLang = i18n.resolvedLanguage || 'tr';
  const activeLocale = localeMap[currentLang] || localeMap.tr;
  const activeFormat = (formatMap[currentLang] || formatMap.tr)[mode];
  const ampmMode = (formatMap[currentLang] || formatMap.tr).ampm;

  const handleOpen = () => { setIsOpen(true); setIsFocused(true); };
  const handleClose = () => { setIsOpen(false); setIsFocused(false); };

  const parsedValue = useMemo(() => {
    if (!value) return null;
    if (mode === 'time' && typeof value === 'string' && /^\d{1,2}:\d{2}(:\d{2})?$/.test(value)) {
      const [h, m, s] = value.split(':').map(Number);
      const d = new Date();
      d.setHours(h, m, s || 0, 0);
      return isValid(d) ? d : null;
    }
    // Backend DateOnly returns "yyyy-MM-dd" — parse as local date so we don't
    // shift a day backward in western timezones.
    if (mode === 'date' && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      const local = new Date(y, m - 1, d);
      // new Date(y, ...) maps years 0-99 to 1900-1999; force the exact year so
      // partial keyboard entry (year typed digit-by-digit: 1 → 19 → 199 → 1990)
      // round-trips through serialize→parse without becoming an invalid/shifted date.
      local.setFullYear(y);
      return isValid(local) ? local : null;
    }
    const date = typeof value === 'string' ? new Date(value) : value;
    return isValid(date) ? date : null;
  }, [value, mode]);

  // Normalize incoming min/max props (string "yyyy-MM-dd" or Date) into
  // Date objects MUI's DatePicker understands.
  const parseBound = (raw) => {
    if (!raw) return undefined;
    if (raw instanceof Date) return isValid(raw) ? raw : undefined;
    if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split('-').map(Number);
      const local = new Date(y, m - 1, d);
      return isValid(local) ? local : undefined;
    }
    const parsed = new Date(raw);
    return isValid(parsed) ? parsed : undefined;
  };
  const parsedMinDate = useMemo(() => parseBound(minDate), [minDate]);
  const parsedMaxDate = useMemo(() => parseBound(maxDate), [maxDate]);

  const serialize = (newValue) => {
    if (!newValue || !isValid(newValue)) return null;
    if (mode === 'time') {
      const hh = String(newValue.getHours()).padStart(2, '0');
      const mm = String(newValue.getMinutes()).padStart(2, '0');
      return `${hh}:${mm}:00`;
    }
    // Backend DateOnly can't parse ISO datetime; emit plain "yyyy-MM-dd" built
    // from local components to avoid timezone day-shift.
    if (mode === 'date') {
      // Zero-pad the year to 4 digits. While the user types the year segment
      // digit-by-digit (1 → 19 → 199 → 1990), MUI fires onChange for each
      // intermediate complete date. Without padding these emit "1-06-15",
      // "19-06-15" etc., which fail the 4-digit parse regex above, round-trip to
      // null, and wipe the field mid-typing — making keyboard date entry impossible.
      const y = String(newValue.getFullYear()).padStart(4, '0');
      const m = String(newValue.getMonth() + 1).padStart(2, '0');
      const d = String(newValue.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    return newValue.toISOString();
  };

  // MUI'nin değişim bağlamı (`ctx`) İKİNCİ argüman olarak aynen iletilir. Yıl
  // hane hane yazılırken her hane geçerli bir tarih ürettiğinden (1 → 0001,
  // 19 → 0019 …) MUI her hane için onChange/onAccept tetikler; yazılan yarım bir
  // yılı gerçek bir seçimden ayırmanın TEK güvenilir yolu bu bağlamdır:
  //   ctx.source === 'view'   → takvimden/görünümden seçildi (kullanıcı bitirdi)
  //   ctx.validationError     → minDate/maxDate dışında (ör. yarım yıl: 0002)
  // Değeri anında bir işleme (filtre uygulama, kaydetme) bağlayan çağıranlar
  // bunu okumalı; yalnız state tutan çağıranlar ikinci argümanı yok sayabilir.
  const handleDateChange = (newValue, ctx) => { onChange?.(serialize(newValue), ctx); };
  const handleAccept = (newValue, ctx) => { onAccept?.(serialize(newValue), ctx); };

  const focused = isFocused || isOpen;
  const hasValue = !!parsedValue;

  const commonProps = {
    value: parsedValue,
    onChange: handleDateChange,
    onAccept: handleAccept,
    onOpen: handleOpen,
    onClose: handleClose,
    format: activeFormat,
    ...(parsedMinDate ? { minDate: parsedMinDate } : {}),
    ...(parsedMaxDate ? { maxDate: parsedMaxDate } : {}),
    slots: { openPickerIcon: mode === 'time' ? Clock : Calendar },
    slotProps: {
      popper: { style: { zIndex: 9999999 } },
      textField: {
        fullWidth: true,
        variant: 'standard',
        onFocus: () => setIsFocused(true),
        onBlur: () => setIsFocused(false),
        InputLabelProps: { shrink: true },
        InputProps: { disableUnderline: true, sx: { paddingLeft: '14px' } },
        sx: {
          '& .MuiInputBase-root': {
            height: '100%', display: 'flex', alignItems: 'center',
            paddingRight: 0, fontFamily: "'Nunito Sans', sans-serif",
            marginTop: '0px !important',
          },
          '& .MuiInputBase-input': {
            padding: '0 0 0 16px !important', height: '100%',
            display: 'flex', alignItems: 'center',
            fontFamily: "'Nunito Sans', sans-serif",
            fontSize: '14px', color: '#004242',
          },
          '& .MuiInputAdornment-root': {
            height: '100%', display: 'flex', alignItems: 'center', maxHeight: 'unset',
          },
        },
      },
      openPickerButton: { disableRipple: true },
    },
  };

  const renderPicker = () => {
    if (mode === 'time') return <TimePicker {...commonProps} ampm={ampmMode} />;
    if (mode === 'datetime') return <MUIDateTimePicker {...commonProps} ampm={ampmMode} />;
    return <DatePicker {...commonProps} />;
  };

  return (
    <ThemeProvider theme={pickerTheme}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={activeLocale}>
        <Container $isFocused={focused} $error={!!error} onClick={() => setIsFocused(true)}>
          <FixedLabel $isFocused={focused} $hasValue={hasValue} $error={!!error}>
            {label}
            {required && <RequiredMark>*</RequiredMark>}
          </FixedLabel>
          <MuiWrapper>{renderPicker()}</MuiWrapper>
        </Container>
        {error && <ErrorText>{error}</ErrorText>}
        {description && <DescriptionText>{description}</DescriptionText>}
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default DateTimePicker;

// #72 (UI düzeltme demeti T6) — "ince varyant" (TextInput.jsx/GenericSelect
// .js'in AYNI gerekçesi): 48px→40px. İç MUI alanları `height:100%` ile
// kademeli olarak bu konteynerden miras aldığından (bkz. commonProps.slotProps
// .textField.sx) tek satır yeterli — MUI-özel iç padding'lere dokunulmadı.
const Container = styled.div`
  position: relative;
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  background-color: #fff;
  border: 1.5px solid ${p => p.$error ? '#E53935' : p.$isFocused ? '#0291EF' : '#E0E4E8'};
  border-radius: 4px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-shadow: ${p => p.$isFocused ? '0 0 0 3px rgba(2, 145, 239, 0.1)' : 'none'};
  cursor: text;

  &:hover {
    border-color: ${p => p.$error ? '#E53935' : p.$isFocused ? '#0291EF' : '#C0C8D0'};
  }
`;

const FixedLabel = styled.label`
  position: absolute;
  top: -8px;
  left: 12px;
  font-size: 11px;
  font-weight: 600;
  font-family: 'Nunito Sans', sans-serif;
  padding: 0 4px;
  z-index: 10;
  background-color: #fff;
  pointer-events: none;
  color: ${p => p.$error ? '#E53935' : p.$isFocused ? '#0291EF' : '#A0A8B4'};
  transition: color 0.2s ease;
`;

const RequiredMark = styled.span`
  color: #E53935;
  margin-left: 2px;
`;

const MuiWrapper = styled.div`
  width: 100%;
  height: 100%;
  .MuiFormControl-root {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
  }
  .MuiInputLabel-root {
    display: none !important;
  }
`;

const ErrorText = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 11px;
  font-family: 'Nunito Sans', sans-serif;
  color: #E53935;
`;

const DescriptionText = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 11px;
  font-family: 'Nunito Sans', sans-serif;
  color: #A0A8B4;
`;
